import React, { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import { RiLoginBoxLine } from "react-icons/ri";

const LoginChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Process data for chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { dates: [], counts: [] };
    }

    // Group logins by date
    const loginsByDate = data.reduce((acc, login) => {
      const date = new Date(login.createdAt).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    // Sort dates
    const sortedDates = Object.keys(loginsByDate).sort((a, b) => 
      new Date(a) - new Date(b)
    );

    // Get counts for each date
    const counts = sortedDates.map(date => loginsByDate[date]);

    return { dates: sortedDates, counts };
  }, [data]);

  // Initialize and update chart
  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            const date = params[0].name;
            const value = params[0].value;
            return `<div style="font-size:14px;color:#666;font-weight:400;line-height:1">${date}</div>
                    <div style="margin:10px 0 0;line-height:1">
                      <div style="margin:0px 0 0;line-height:1">
                        <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${params[0].color};"></span>
                        <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">User Logins</span>
                        <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${value}</span>
                        <div style="clear:both"></div>
                      </div>
                    </div>`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: chartData.dates,
          axisLabel: {
            rotate: chartData.dates.length > 5 ? 45 : 0,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1
        },
        series: [
          {
            name: 'User Logins',
            type: 'line',
            smooth: true,
            data: chartData.counts,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: 'rgba(250, 204, 20, 0.5)' // Yellow with opacity
                }, {
                  offset: 1, color: 'rgba(250, 204, 20, 0.05)'
                }]
              }
            },
            lineStyle: {
              width: 3,
              color: '#facc14'
            },
            itemStyle: {
              color: '#facc14',
              borderWidth: 2
            },
            emphasis: {
              itemStyle: {
                borderWidth: 4,
                shadowBlur: 10,
                shadowColor: 'rgba(250, 204, 20, 0.5)'
              }
            }
          }
        ],
        toolbox: {
          feature: {
            saveAsImage: { title: 'Save as Image' },
            dataView: { title: 'Data View', lang: ['Data View', 'Close', 'Refresh'] },
            magicType: { 
              type: ['line', 'bar'], 
              title: { 
                line: 'Switch to Line',
                bar: 'Switch to Bar'
              }
            },
            restore: { title: 'Restore' }
          }
        }
      };

      chartInstance.current.setOption(option);
    }

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [chartData]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="bg-yellow-500 p-2 rounded-lg shadow-md text-white mr-3">
            <RiLoginBoxLine size={20} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800">User Logins</h2>
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-semibold">{data?.length || 0}</span> total logins
        </div>
      </div>
      
      {data && data.length > 0 ? (
        <div ref={chartRef} className="h-[300px] w-full" />
      ) : (
        <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No login data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

export default LoginChart;